import { memo, ReactElement, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

interface FilterFormDataConsumerProps {
  children: (props: { formData: Record<string, any> }) => ReactElement | null;
  watch?: string[];
  alwaysOn?: boolean;
  style?: React.CSSProperties;
}

/**
 * Replacement for FormDataConsumer that doesn't cause filter flashing in React Admin v5.
 * Uses useWatch from react-hook-form to subscribe to specific form values without
 * causing full form re-renders.
 *
 * @see https://github.com/marmelab/react-admin/issues/10415
 */
const FilterFormDataConsumer = memo(({ children, watch: watchFields, ...rest }: FilterFormDataConsumerProps) => {
  const { getValues } = useFormContext();

  // Watch only specified fields, or all form values if not specified
  const watchedValues = useWatch({ name: watchFields });

  // Create formData object from watched values
  const formData = useMemo(() => {
    if (watchFields && Array.isArray(watchedValues)) {
      const data: Record<string, any> = {};
      watchFields.forEach((field, index) => {
        data[field] = watchedValues[index];
      });
      return data;
    }
    // Fallback to getting all values if no specific fields are specified
    return getValues();
  }, [watchedValues, watchFields, getValues]);

  return children({ formData });
});

FilterFormDataConsumer.displayName = 'FilterFormDataConsumer';

export default FilterFormDataConsumer;
